import { PaginationState } from 'app/shared/pagination/pagination.model';
import { useMutation, useQuery } from '@tanstack/react-query';
import { copyBuilding, copyBuildings, copyConstruction, copyConstructions, copyIntervention, copyInterventions, deleteBuilding, deleteConstruction, deleteIntervention, fetchBuilding, fetchBuildings, fetchConstruction, fetchConstructionBuildingProcedureCodebooks, fetchConstructions, fetchIntervention, fetchInterventions, getConstructionBuildingProcedureVariant, saveBuilding, saveConstruction, saveConstructionBuildingProcedureCodebooks, saveIntervention } from 'app/edozvola/cases/construction-building-procedure/service/construction-building-procedure.service';
import { showToast } from 'app/shared/util/toast.util';
import { queryClient } from 'app/app';
import { toast } from 'react-toastify';
import { Building } from 'app/edozvola/cases/construction-building-procedure/buildings/model/building.model';
import { Construction } from 'app/edozvola/cases/construction-building-procedure/constructions/model/construction.model';
import { Intervention } from '../interventions/model/intervention.model';
import { ConstructionBuildingProcedure } from 'app/edozvola/cases/construction-building-procedure/model/construction-building-procedure.model';
import { messages, TABLE_QUERY_KEYS } from 'app/config/constants';
import success = toast.success;
import error = toast.error;

export const fetchBuildingsQuery = (pagination: PaginationState, idCase: string) => {
  return useQuery({
    queryKey: [TABLE_QUERY_KEYS.BUILDINGS_TABLE, { idCase: idCase, pagination: { ...pagination } }],
    queryFn: ({ signal }) => fetchBuildings(idCase, pagination.page, pagination.size, pagination.column, pagination.direction),
  });
};

export const fetchConstructionBuildingProcedureCodebooksQuery = (idCase: string) => {
  return useQuery({
    queryKey: ['cbp-codebooks'],
    queryFn: () => fetchConstructionBuildingProcedureCodebooks(idCase),
  });
};

export const saveConstructionBuildingProcedureCodebooksQuery = (idCase: string, onSuccessCallback) => {
  return useMutation({
    mutationFn: (cbp: ConstructionBuildingProcedure) => {
      return saveConstructionBuildingProcedureCodebooks(idCase, cbp);
    },
    onSuccess: () => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: ['cbp-codebooks'] });
      onSuccessCallback();
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchBuildingQuery = (idBuilding: string, variant: string) => {
  return useQuery({
    queryKey: ['building', idBuilding],
    queryFn: () => fetchBuilding(idBuilding, variant),
    enabled: !!idBuilding,
  });
};

export const saveBuildingQuery = (idCase: string, variant: string, onSuccessCallback) => {
  return useMutation({
    mutationFn: (building: Building) => {
      return saveBuilding(idCase, variant, building);
    },
    onSuccess: () => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.BUILDINGS_TABLE] });
      onSuccessCallback();
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const deleteBuildingQuery = (variant: string) => {
  return useMutation({
    mutationFn: (idBuilding: number) => deleteBuilding(idBuilding, variant),
    onSuccess: () => {
      showToast(success, messages.DATA_DELETED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.BUILDINGS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const copyBuildingQuery = (variant: string) => {
  return useMutation({
    mutationFn: (idBuilding: number) => copyBuilding(idBuilding, variant),
    onSuccess: () => {
      showToast(success, messages.DATA_COPIED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.BUILDINGS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchConstructionsQuery = (pagination: PaginationState, idCase: string) => {
  return useQuery({
    queryKey: [TABLE_QUERY_KEYS.CONSTRUCTIONS_TABLE, { idCase: idCase, pagination: { ...pagination } }],
    queryFn: ({ signal }) => fetchConstructions(idCase, pagination.page, pagination.size, pagination.column, pagination.direction),
  });
};

export const fetchConstructionQuery = (idConstruction: string, variant: string) => {
  return useQuery({
    queryKey: ['construction', idConstruction],
    queryFn: () => fetchConstruction(idConstruction, variant),
    enabled: !!idConstruction,
  });
};

export const saveConstructionQuery = (idCase: string, variant: string, onSuccessCallback) => {
  return useMutation({
    mutationFn: (construction: Construction) => {
      return saveConstruction(idCase, variant, construction);
    },
    onSuccess: () => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.CONSTRUCTIONS_TABLE] });
      onSuccessCallback();
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const deleteConstructionQuery = (variant: string) => {
  return useMutation({
    mutationFn: (idConstruction: number) => deleteConstruction(idConstruction, variant),
    onSuccess: () => {
      showToast(success, messages.DATA_DELETED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.CONSTRUCTIONS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const copyConstructionQuery = (variant: string) => {
  return useMutation({
    mutationFn: (idConstruction: number) => copyConstruction(idConstruction, variant),
    onSuccess: () => {
      showToast(success, messages.DATA_COPIED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.CONSTRUCTIONS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchInterventionsQuery = (pagination: PaginationState, idCase: string) => {
  return useQuery({
    queryKey: [TABLE_QUERY_KEYS.INTERVENTIONS_TABLE, { idCase: idCase, pagination: { ...pagination } }],
    queryFn: ({ signal }) => fetchInterventions(idCase, pagination.page, pagination.size, pagination.column, pagination.direction),
  });
};

export const fetchInterventionQuery = (idIntervention: string, variant: string) => {
  return useQuery({
    queryKey: ['intervention', idIntervention],
    queryFn: () => fetchIntervention(idIntervention, variant),
    enabled: !!idIntervention,
  });
};

export const saveInterventionQuery = (idCase: string, variant: string, onSuccessCallback) => {
  return useMutation({
    mutationFn: (intervention: Intervention) => {
      return saveIntervention(idCase, variant, intervention);
    },
    onSuccess: () => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.INTERVENTIONS_TABLE] });
      onSuccessCallback();
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const deleteInterventionQuery = (variant: string) => {
  return useMutation({
    mutationFn: (idIntervention: number) => deleteIntervention(idIntervention, variant),
    onSuccess: () => {
      showToast(success, messages.DATA_DELETED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.INTERVENTIONS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const copyInterventionQuery = (variant: string) => {
  return useMutation({
    mutationFn: (idIntervention: number) => copyIntervention(idIntervention, variant),
    onSuccess: () => {
      showToast(success, messages.DATA_COPIED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.INTERVENTIONS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const copyBuildingsFromCaseQuery = (caseUuid: string, variant: string, onSuccessCallback: any) => {
  return useMutation({
    mutationFn: (caseWebIdToCopyFrom: string) => copyBuildings(caseUuid, variant, caseWebIdToCopyFrom),
    onSuccess: () => {
      onSuccessCallback();
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.BUILDINGS_TABLE] });
      showToast(success, messages.DATA_COPIED);
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const copyInterventionsFromCaseQuery = (caseUuid: string, variant: string, onSuccessCallback: any) => {
  return useMutation({
    mutationFn: (caseWebIdToCopyFrom: string) => copyInterventions(caseUuid, variant, caseWebIdToCopyFrom),
    onSuccess: () => {
      onSuccessCallback();
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.INTERVENTIONS_TABLE] });
      showToast(success, messages.DATA_COPIED);
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const copyConstructionsFromCaseQuery = (caseUuid: string, variant: string, onSuccessCallback: any) => {
  return useMutation({
    mutationFn: (caseWebIdToCopyFrom: string) => copyConstructions(caseUuid, variant, caseWebIdToCopyFrom),
    onSuccess: () => {
      onSuccessCallback();
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.CONSTRUCTIONS_TABLE] });
      showToast(success, messages.DATA_COPIED);
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchConstructionBuildingProcedureVariantQuery = (idCase: string, caseInPreview: boolean) => {
  return useQuery({
    queryKey: ['construction-building-procedure-variant', { idCase: idCase, caseInPreview: caseInPreview }],
    queryFn: () => getConstructionBuildingProcedureVariant(idCase, caseInPreview),
  });
};
